import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bell, X, CheckCheck } from "lucide-react";
import { toast } from "sonner";

export default function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const lastCountRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      // Toast on any newly unread since last poll
      const newUnread = data.filter(n => !n.read).length;
      if (newUnread > lastCountRef.current && lastCountRef.current !== 0) {
        const latest = data.find(n => !n.read);
        if (latest) toast(latest.title, { description: latest.body });
      }
      lastCountRef.current = newUnread;
      setNotifs(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const unread = notifs.filter(n => !n.read).length;

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-full border border-[#262626] bg-[#0a0a0a] text-gray-300 hover:text-[#d4af37] hover:border-[#d4af37]/60 transition-colors flex items-center justify-center"
        data-testid="notif-bell"
      >
        <Bell className="w-4 h-4" strokeWidth={1.5}/>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#d4af37] text-black text-[10px] font-bold flex items-center justify-center px-1" data-testid="notif-count">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-12 right-0 w-96 iz-card p-0 overflow-hidden shadow-2xl" data-testid="notif-panel">
          <div className="flex items-center justify-between p-4 border-b border-[#262626]">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">Notifications</p>
              <p className="text-sm text-white mt-0.5">{unread} unread</p>
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-gray-400 hover:text-[#d4af37] flex items-center gap-1" data-testid="notif-mark-all">
                  <CheckCheck className="w-3.5 h-3.5"/> Mark all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white">
                <X className="w-4 h-4"/>
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {notifs.length === 0 ? (
              <p className="p-6 text-center text-xs text-gray-500">No notifications yet.</p>
            ) : notifs.map(n => (
              <div key={n.id} className={`p-4 border-b border-[#1a1a1a] last:border-0 ${!n.read ? "bg-[#d4af37]/[0.03]" : ""}`}>
                <div className="flex items-start gap-3">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${!n.read ? "bg-[#d4af37]" : "bg-gray-700"}`}/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{n.title}</p>
                    {n.body && <p className="text-xs text-gray-500 mt-1">{n.body}</p>}
                    <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mt-2">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
